import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

const SEPARATOR = '\u00b7';

const DEFAULT_RULE = { field: 'customer_email', operator: 'is_empty', value: '' };

function Automations({ token, onAuthError, tenant }) {
  const isAdmin = !!(tenant && tenant.role === 'admin');
  const { t } = useTranslation();
  const TRIGGER_LABELS = {
    low_stock: t('automations.triggerLowStock'),
    margin_alert: t('automations.triggerMarginAlert'),
    order_status: t('automations.triggerOrderRule'),
  };

  const RULE_FIELDS = {
    customer_email: { label: t('automations.fieldCustomerEmail'), operators: ['is_empty', 'is_not_empty', 'equals'], valueType: 'text' },
    customer_name: { label: t('automations.fieldCustomerName'), operators: ['is_empty', 'is_not_empty'], valueType: 'text' },
    total_net: { label: t('automations.fieldTotalNet'), operators: ['<', '<=', '>', '>='], valueType: 'number' },
    source_platform: { label: t('automations.fieldSourcePlatform'), operators: ['equals'], valueType: 'text' },
    status: { label: t('automations.fieldStatus'), operators: ['equals'], valueType: 'text' },
  };

  const OPERATOR_LABELS = {
    is_empty: t('automations.operatorIsEmpty'),
    is_not_empty: t('automations.operatorIsNotEmpty'),
    equals: t('automations.operatorEquals'),
    '<': t('automations.operatorLt'),
    '<=': t('automations.operatorLte'),
    '>': t('automations.operatorGt'),
    '>=': t('automations.operatorGte'),
  };

  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newAutomation, setNewAutomation] = useState({ name: '', trigger_type: 'low_stock', value: 10, rules: [{ ...DEFAULT_RULE }] });
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [noteMessage, setNoteMessage] = useState('');
  const [teamNotes, setTeamNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  useEffect(() => {
    fetchAutomations();
    if (isAdmin) fetchTeamNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const sendNote = async (e) => {
    e.preventDefault();
    setSendingNote(true);
    setNoteMessage('');
    try {
      await axios.post(`${API_URL}/api/v1/automation-notes`, { note: noteText }, authHeaders);
      setNoteMessage(t('automations.noteSent'));
      setNoteText('');
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setNoteMessage(t('automations.noteError'));
    } finally {
      setSendingNote(false);
    }
  };

  const fetchTeamNotes = async () => {
    setLoadingNotes(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/automation-notes`, authHeaders);
      setTeamNotes(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
    } finally {
      setLoadingNotes(false);
    }
  };

  const fetchAutomations = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/automations`, authHeaders);
      setAutomations(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('automations.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (automation) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/v1/automations/${automation.id}`,
        { is_active: !automation.is_active },
        authHeaders
      );
      setAutomations((prev) => prev.map((a) => (a.id === automation.id ? response.data.data : a)));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('automations.updateError'));
    }
  };

  const deleteAutomation = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/v1/automations/${id}`, authHeaders);
      setAutomations((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setError(t('automations.deleteError'));
    }
  };

  const updateRule = (idx, patch) => {
    setNewAutomation((v) => ({
      ...v,
      rules: v.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const addRule = () => {
    setNewAutomation((v) => ({ ...v, rules: [...v.rules, { ...DEFAULT_RULE }] }));
  };

  const removeRule = (idx) => {
    setNewAutomation((v) => ({ ...v, rules: v.rules.filter((_, i) => i !== idx) }));
  };

  const describeRules = (rules) => {
    if (!rules || rules.length === 0) return '';
    return rules
      .map((r) => {
        const fieldLabel = RULE_FIELDS[r.field]?.label || r.field;
        const opLabel = OPERATOR_LABELS[r.operator] || r.operator;
        if (r.operator === 'is_empty' || r.operator === 'is_not_empty') return `${fieldLabel} ${opLabel}`;
        return `${fieldLabel} ${opLabel} ${r.value}`;
      })
      .join(` ${t('automations.ruleAnd')} `);
  };

  const createAutomation = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload =
        newAutomation.trigger_type === 'order_status'
          ? {
              name: newAutomation.name,
              trigger_type: newAutomation.trigger_type,
              rules: newAutomation.rules.map((r) => ({
                field: r.field,
                operator: r.operator,
                value:
                  r.operator === 'is_empty' || r.operator === 'is_not_empty'
                    ? null
                    : RULE_FIELDS[r.field].valueType === 'number'
                    ? parseFloat(r.value)
                    : r.value,
              })),
            }
          : { name: newAutomation.name, trigger_type: newAutomation.trigger_type, value: newAutomation.value };

      const response = await axios.post(`${API_URL}/api/v1/automations`, payload, authHeaders);
      setAutomations((prev) => [...prev, response.data.data]);
      setShowForm(false);
      setNewAutomation({ name: '', trigger_type: 'low_stock', value: 10, rules: [{ ...DEFAULT_RULE }] });
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('automations.createError'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-white text-2xl font-bold">{t('automations.title')}</h2>
          <p className="text-muted text-sm mt-1">{t('automations.subtitle')}</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="self-start sm:self-auto bg-accent text-panel font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer"
          >
            {showForm ? t('automations.cancel') : t('automations.newAutomation')}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={createAutomation} className="bg-surface border border-edge rounded-2xl p-5 mb-6 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="text-muted text-xs block mb-1.5">{t('automations.name')}</label>
              <input
                type="text"
                required
                value={newAutomation.name}
                onChange={(e) => setNewAutomation((v) => ({ ...v, name: e.target.value }))}
                className="w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
                placeholder={t('automations.namePlaceholder')}
              />
            </div>
            <div>
              <label className="text-muted text-xs block mb-1.5">{t('automations.type')}</label>
              <select
                value={newAutomation.trigger_type}
                onChange={(e) => setNewAutomation((v) => ({ ...v, trigger_type: e.target.value }))}
                className="bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              >
                <option value="low_stock">{t('automations.triggerLowStock')}</option>
                <option value="margin_alert">{t('automations.triggerMarginAlert')}</option>
                <option value="order_status">{t('automations.triggerOrderRule')}</option>
              </select>
            </div>
            {newAutomation.trigger_type !== 'order_status' && (
              <div>
                <label className="text-muted text-xs block mb-1.5">{t('automations.threshold')}</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={newAutomation.value}
                  onChange={(e) => setNewAutomation((v) => ({ ...v, value: e.target.value }))}
                  className="w-24 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
                />
              </div>
            )}
          </div>

          {newAutomation.trigger_type === 'order_status' && (
            <div className="flex flex-col gap-2">
              <label className="text-muted text-xs">{t('automations.rulesLabel')}</label>
              {newAutomation.rules.map((rule, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <select
                    value={rule.field}
                    onChange={(e) =>
                      updateRule(idx, {
                        field: e.target.value,
                        operator: RULE_FIELDS[e.target.value].operators[0],
                        value: '',
                      })
                    }
                    className="bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                  >
                    {Object.keys(RULE_FIELDS).map((f) => (
                      <option key={f} value={f}>
                        {RULE_FIELDS[f].label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={rule.operator}
                    onChange={(e) => updateRule(idx, { operator: e.target.value, value: '' })}
                    className="bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                  >
                    {RULE_FIELDS[rule.field].operators.map((op) => (
                      <option key={op} value={op}>
                        {OPERATOR_LABELS[op]}
                      </option>
                    ))}
                  </select>
                  {rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty' && (
                    <input
                      type={RULE_FIELDS[rule.field].valueType}
                      value={rule.value}
                      onChange={(e) => updateRule(idx, { value: e.target.value })}
                      className="bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm flex-1 focus:outline-none focus:border-accent"
                      placeholder={t('automations.valuePlaceholder')}
                    />
                  )}
                  {newAutomation.rules.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRule(idx)}
                      className="text-red-400 text-xs px-2 py-1 rounded border border-[#7f1d1d] cursor-pointer self-start sm:self-auto"
                    >
                      {t('automations.removeRule')}
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addRule}
                className="self-start text-accent text-xs font-semibold cursor-pointer"
              >
                + {t('automations.addRule')}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="self-start bg-accent text-panel font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer"
          >
            {saving ? t('automations.creating') : t('automations.create')}
          </button>
        </form>
      )}

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6">
        {loading ? (
          <p className="text-muted text-center py-12">{t('automations.loading')}</p>
        ) : automations.length === 0 ? (
          <p className="text-muted text-center py-12">{t('automations.noneConfigured')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {automations.map((auto) => (
              <div key={auto.id} className="bg-surfaceAlt border border-edge rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-white text-sm font-semibold">{auto.name}</p>
                  <p className="text-muted text-xs mt-1">
                    {TRIGGER_LABELS[auto.trigger_type] || auto.trigger_type} {SEPARATOR}{' '}
                    {auto.trigger_type === 'order_status'
                      ? describeRules(auto.condition?.rules)
                      : `${t('automations.thresholdLabel')}: ${auto.condition?.value}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {isAdmin ? (
                    <button
                      onClick={() => toggleActive(auto)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer ${
                        auto.is_active
                          ? 'bg-[#0f2a0f] text-accent border-[#166534]'
                          : 'bg-edge text-muted border-edge'
                      }`}
                    >
                      {auto.is_active ? t('automations.active') : t('automations.paused')}
                    </button>
                  ) : (
                    <span
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                        auto.is_active
                          ? 'bg-[#0f2a0f] text-accent border-[#166534]'
                          : 'bg-edge text-muted border-edge'
                      }`}
                    >
                      {auto.is_active ? t('automations.active') : t('automations.paused')}
                    </span>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => deleteAutomation(auto.id)}
                      className="text-red-400 text-xs px-3 py-1.5 rounded-lg border border-[#7f1d1d] cursor-pointer"
                    >
                      {t('automations.delete')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isAdmin && (
        <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mt-6">
          <p className="text-white text-sm font-semibold mb-1">{t('automations.leaveNoteTitle')}</p>
          <p className="text-muted text-xs mb-4">{t('automations.leaveNoteHint')}</p>
          <form onSubmit={sendNote} className="flex flex-col gap-3">
            <textarea
              required
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={t('automations.notePlaceholder')}
              rows={3}
              className="bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent resize-none"
            />
            <button
              type="submit"
              disabled={sendingNote}
              className={`self-start bg-accent text-panel font-bold text-sm px-5 py-2 rounded-lg cursor-pointer ${sendingNote ? 'opacity-70' : ''}`}
            >
              {sendingNote ? t('automations.sendingNote') : t('automations.sendNote')}
            </button>
            {noteMessage && <p className="text-muted text-xs">{noteMessage}</p>}
          </form>
        </div>
      )}

      {isAdmin && !loadingNotes && teamNotes.length > 0 && (
        <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mt-6">
          <p className="text-white text-sm font-semibold mb-4">{t('automations.teamNotesTitle')}</p>
          <div className="flex flex-col gap-3">
            {teamNotes.map((n) => (
              <div key={n.id} className="bg-surfaceAlt border border-edge rounded-lg p-3">
                <p className="text-white text-sm">{n.note}</p>
                <p className="text-muted text-xs mt-1">{n.author_email}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Automations;